import React from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import Container from 'components/Container';
import Form from 'components/Form';
import HeroSection from 'components/HeroSection';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Stack from 'components/Stack';
import Text from 'components/Text';
import formatAirDate from 'utils/formatAirDate';
import useForm from 'hooks/useForm';
import useTrackEvent from 'hooks/useTrackEvent';
import { PAGE_REGIONS } from 'utils';
import { PostShape } from 'utils/props';
import { color, media } from 'styles/utils';

const StyledBreadcrumb = styled(Link)`
  text-decoration: none;
`;

const StyledSection = styled(HeroSection)`
  overflow: visible;
`;

const StyledContainer = styled(Container)`
  margin-bottom: -10rem;
`;

const StyledHeaderMedia = styled.div`
  border-radius: 0.5rem;
  left: 50%;
  overflow: hidden;
  position: relative;
  transform: translateX(-50%);
  width: 100%;

  ${media.max('lg')} {
    width: 100%;
  }
`;

const StyledForm = styled(Form)`
  background: ${color('darkTeal')};
  border-radius: 0.5rem;
  padding: 2rem;
  width: 100%;

  ${media.max('md')} {
    padding: 1rem;
  }

  & > * {
    max-width: 32rem;
  }
`;

export default function UpcomingEpisodeHero({ post }) {
  const { trackEvent } = useTrackEvent();
  const { date, time } = formatAirDate(post.videoAirDate);

  const form = useForm({
    onSubmit: (event, { formValues, submitterValue }) =>
      trackEvent('content_subscribed', {
        object: 'content',
        action: 'subscribed',
        label: submitterValue,
        location: PAGE_REGIONS.hero,
        pageUrl: window.location.href,
        content_name: post.title,
        email: formValues.email,
      }),
  });

  return (
    <StyledSection variant="minimal">
      <StyledContainer size="sm">
        <Stack gap="2rem" align="start">
          <Text variant="smallP" color="lightTeal">
            <StyledBreadcrumb to={`/resources/${post.category.slug}`}>
              {post.category.title}
            </StyledBreadcrumb>
            <span>{' | '}</span>
            <StyledBreadcrumb
              to={`/resources/${post.category.slug}/${post.subcategory.slug}`}
            >
              {post.subcategory.title}
            </StyledBreadcrumb>
          </Text>

          <Text as="h1" variant="f1" color="white">
            {post.title}
          </Text>

          <div>
            <Text variant="smallP" color="textGray">
              {date}
            </Text>
            <Text variant="smallP" color="textGray">
              {time}
            </Text>
          </div>

          <StyledForm {...form.getFormProps()}>
            <Text variant="smallP" color="textGray">
              Enter your email to get the video the day it drops 👇
            </Text>
            {form.isSubmitted ? (
              <Form.Message>
                {form.isSubmitFailure &&
                  'Something went wrong. Please try again later.'}
                {form.isSubmitSuccess &&
                  "Thank you! You'll receive the invite via email."}
              </Form.Message>
            ) : (
              <Form.ControlGroup>
                <Form.Control
                  aria-label="Enter your work email"
                  autocomplete="email"
                  name="email"
                  placeholder="Enter your work email"
                  required
                  type="email"
                />
                <Form.Submitter>
                  Sign Up For Episode &ensp;
                  <Icon name="arrow-up-right" size="sm" />
                </Form.Submitter>
              </Form.ControlGroup>
            )}
          </StyledForm>

          <StyledHeaderMedia>
            {post.featureImage && (
              <GatsbyImage
                image={getImage(post.featureImage?.image.asset)}
                alt={post.featureImage?.alt || post.title}
              />
            )}
          </StyledHeaderMedia>
        </Stack>
      </StyledContainer>
    </StyledSection>
  );
}

UpcomingEpisodeHero.propTypes = {
  post: PostShape,
};
